import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';

export interface TabItem {
  label: React.ReactNode;
  value: string;
  count?: number | string;
}

export interface TabToggleProperties {
  tabs: (string | TabItem)[]; // List of tab names or complex items
  defaultTab?: string; // Optional default label or value
  activeTab?: string; // Controlled active tab value or label
  value?: string; // Controlled tab value (alias for activeTab)
  onChange?: (value: string) => void; // Callback with the selected value
  isLabelValue?: boolean; // Prop to use label-value model
  maxVisible?: number; // Maximum number of tabs to show initially
}

/**
 * Tab toggle component
 * @param {any} param0 - The param0
 * @param {any} param0.tabs - The tabs prop
 * @param {any} param0.defaultTab - The defaultTab prop
 * @param {any} param0.onChange - The onChange prop
 * @param {any} param0.isLabelValue - The isLabelValue prop
 * @param {any} param0.maxVisible - The maxVisible prop
 * @returns {any} The rendered tab toggle component.
 */
function TabToggle({
  tabs,
  defaultTab,
  activeTab,
  value,
  onChange,
  isLabelValue = false,
  maxVisible = 8,
}: TabToggleProperties): React.JSX.Element {
  const normalizedTabs: TabItem[] = useMemo(
    () => tabs.map((tab) => (typeof tab === 'string' ? { label: tab, value: tab } : tab)),
    [tabs],
  );

  /**
   * Returns the identifier for a tab item based on the isLabelValue property.
   * @param {TabItem} [tab] - The tab item.
   * @returns {string} The resolved tab identifier.
   */
  const getIdentifier = useCallback(
    (tab?: TabItem): string => {
      if (!tab) return '';
      if (isLabelValue) {
        return tab.value ?? '';
      }
      if (typeof tab.label === 'string') {
        return tab.label;
      }
      return tab.value ?? '';
    },
    [isLabelValue],
  );

  /**
   * Finds a tab item by its label or value matching the given identifier.
   * @param {string} [target] - The target label or value string.
   * @returns {TabItem | undefined} The found tab item or undefined.
   */
  const findTab = useCallback(
    (target?: string): TabItem | undefined => {
      if (!target) return undefined;
      return normalizedTabs.find((t) => {
        if (isLabelValue) return t.value === target;
        return (typeof t.label === 'string' ? t.label : t.value) === target;
      });
    },
    [normalizedTabs, isLabelValue],
  );

  const controlledTabProperty = activeTab ?? value;
  const initialTarget = controlledTabProperty ?? defaultTab;
  const initialTab = findTab(initialTarget) || normalizedTabs[0];
  const [activeTabId, setActiveTabId] = useState<string>(getIdentifier(initialTab));
  const [underlineStyle, setUnderlineStyle] = useState<{ left: number; width: number }>({
    left: 0,
    width: 0,
  });

  const effectiveActiveTabId =
    controlledTabProperty !== undefined
      ? getIdentifier(findTab(controlledTabProperty)) || controlledTabProperty
      : activeTabId;

  const tabReferences = useRef<(HTMLButtonElement | null)[]>([]);
  const scrollContainerReference = useRef<HTMLDivElement>(null);
  const [maxWidth, setMaxWidth] = useState<number | string>('none');
  const onChangeReference = useRef(onChange);

  useEffect(() => {
    onChangeReference.current = onChange;
  }, [onChange]);

  // Keep activeTabId valid if normalizedTabs or defaultTab change
  useEffect(() => {
    if (normalizedTabs.length === 0) return;

    const currentActiveExists = normalizedTabs.some((t) => getIdentifier(t) === activeTabId);
    if (!currentActiveExists) {
      const fallbackTab = findTab(defaultTab) || normalizedTabs[0];
      if (fallbackTab) {
        const newId = getIdentifier(fallbackTab);
        setActiveTabId(newId);
      }
    }
  }, [normalizedTabs, defaultTab, activeTabId, findTab, getIdentifier]);

  // Keep activeTabId in sync with controlled props changes
  useEffect(() => {
    if (controlledTabProperty !== undefined) {
      const matched = findTab(controlledTabProperty);
      if (matched) {
        const newId = getIdentifier(matched);
        if (newId !== activeTabId) {
          setActiveTabId(newId);
        }
      }
    }
  }, [controlledTabProperty, findTab, getIdentifier, activeTabId]);

  const hasOverflow = normalizedTabs.length > maxVisible;
  const remainingCount = hasOverflow ? normalizedTabs.length - maxVisible : 0;

  // Calculate width of first maxVisible tabs to restrict initial view
  useEffect(() => {
    if (hasOverflow) {
      /**
       * Calculates the total width of the first maxVisible tabs to restrict the initial view.
       */
      const calculateWidth = () => {
        let totalWidth = 0;
        for (let index = 0; index < maxVisible; index += 1) {
          const tab = tabReferences.current[index];
          if (tab) {
            totalWidth += tab.getBoundingClientRect().width;
          }
        }
        if (totalWidth > 0) {
          setMaxWidth(totalWidth);
        }
      };

      calculateWidth();
      const timer = setTimeout(calculateWidth, 100);
      return () => clearTimeout(timer);
    }
    setMaxWidth('none');
    return undefined;
  }, [hasOverflow, normalizedTabs, maxVisible]);

  /**
   * Recalculates and updates the underline indicator position and dimensions.
   */
  const updateUnderline = useCallback(() => {
    const index = normalizedTabs.findIndex((t) => getIdentifier(t) === effectiveActiveTabId);
    if (index === -1) {
      setUnderlineStyle({ left: 0, width: 0 });
      return;
    }

    const currentTab = tabReferences.current[index];
    const scrollContainer = scrollContainerReference.current;
    if (currentTab && scrollContainer) {
      const { offsetLeft, offsetWidth } = currentTab;
      if (offsetWidth > 0) {
        setUnderlineStyle({
          left: offsetLeft,
          width: offsetWidth,
        });
      }

      // Auto-scroll into view if tab is outside viewport
      const tabLeft = offsetLeft;
      const tabWidth = offsetWidth;
      const containerScrollLeft = scrollContainer.scrollLeft;
      const containerWidth = scrollContainer.offsetWidth;

      if (containerWidth > 0 && tabWidth > 0) {
        if (tabLeft < containerScrollLeft) {
          scrollContainer.scrollTo({ left: tabLeft, behavior: 'smooth' });
        } else if (tabLeft + tabWidth > containerScrollLeft + containerWidth) {
          scrollContainer.scrollTo({
            left: tabLeft + tabWidth - containerWidth,
            behavior: 'smooth',
          });
        }
      }
    }
  }, [effectiveActiveTabId, normalizedTabs, getIdentifier]);

  // Adjust underline position dynamically
  useEffect(() => {
    updateUnderline();

    const frameId = requestAnimationFrame(() => {
      updateUnderline();
    });

    const timerId = setTimeout(() => {
      updateUnderline();
    }, 100);

    return () => {
      cancelAnimationFrame(frameId);
      clearTimeout(timerId);
    };
  }, [updateUnderline, maxWidth]);

  // Handle resize observer and font load
  useEffect(() => {
    const container = scrollContainerReference.current;
    if (!container) return undefined;

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updateUnderline();
      });
      resizeObserver.observe(container);
    }

    /**
     * Handles window resize events to update the underline.
     */
    const handleWindowResize = () => {
      updateUnderline();
    };

    window.addEventListener('resize', handleWindowResize);

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        updateUnderline();
      });
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [updateUnderline]);

  /**
   * Handles clicking a tab button to switch the active tab.
   * @param {string} tabId - The identifier of the clicked tab.
   */
  const handleTabClick = (tabId: string) => {
    if (tabId !== effectiveActiveTabId) {
      if (controlledTabProperty === undefined) {
        setActiveTabId(tabId);
      }
      onChangeReference.current?.(tabId);
    }
  };

  /**
   * Handles the click event for the "Show more" badge to scroll the tab list.
   */
  const handleMoreClick = () => {
    if (scrollContainerReference.current) {
      const isAtEnd =
        scrollContainerReference.current.scrollLeft +
          scrollContainerReference.current.offsetWidth >=
        scrollContainerReference.current.scrollWidth - 10;

      scrollContainerReference.current.scrollTo({
        left: isAtEnd ? 0 : scrollContainerReference.current.scrollWidth,
        behavior: 'smooth',
      });
    }
  };

  return (
    <div className="tab-toggle-wrap">
      <div
        className="tab-toggle"
        role="tablist"
        ref={scrollContainerReference}
        style={{ maxWidth }}>
        {normalizedTabs.map((tab, index) => {
          const tabId = getIdentifier(tab);
          return (
            <button
              key={tabId || `tab-${index}`}
              type="button"
              role="tab"
              aria-selected={effectiveActiveTabId === tabId}
              ref={(element) => {
                tabReferences.current[index] = element;
              }}
              className={`tab ${effectiveActiveTabId === tabId ? 'active' : ''}`}
              onClick={() => handleTabClick(tabId)}>
              {tab.label}
              {tab.count !== undefined && <span className="tab-count">{tab.count}</span>}
            </button>
          );
        })}
        <span
          className="underline"
          style={{ left: underlineStyle.left, width: underlineStyle.width }}
        />
      </div>
      {hasOverflow && (
        <button
          type="button"
          className="remaining-tabs-badge"
          onClick={handleMoreClick}
          title="Show more">
          +{remainingCount}
        </button>
      )}
    </div>
  );
}

export default TabToggle;

TabToggle.defaultProps = {
  defaultTab: '',
  activeTab: undefined,
  value: undefined,
  onChange: null,
  isLabelValue: false,
  maxVisible: 8,
};
